import { ColDef, ICellRendererParams } from 'ag-grid-community';

import { Volume } from '~/shared/types/api';
import Tag from '~/shared/components/Tag';
import Tooltip from '~/shared/components/Tooltip';
import SizeCell from '~/shared/components/SizeCell';

import VolumeTagsRenderer from '../../../AllVolumesPage/AllVolumesTable/VolumeTagsRenderer';

// Type Definition
export type AutoscaleVolume = {
  volumeId: string;
  cluster: string;
  nodeGroup: string;
  instanceId: string;
  tags: { key: string; value: string }[];
  datafyDiskSize: number;
  supported: boolean | null;
};

export const getColumnDefs = ({
  sortField,
  windowWidth,
}: {
  sortField: { field: string; dir: 'desc' | 'asc' };
  windowWidth: number;
}): ColDef<AutoscaleVolume>[] => [
  {
    field: 'volumeId',
    headerName: 'Name',
    initialWidth: 195,
    minWidth: 195,
    maxWidth: 195,
    cellRenderer: ({ data }: ICellRendererParams<AutoscaleVolume>) => {
      console.log(data?.volumeId, 'data?.volumeId');
      return data?.volumeId ? <span className="text-primary-900">{data.volumeId}</span> : null;
    },
  },
  {
    field: 'cluster',
    headerName: 'Cluster',
    sort: sortField?.field === 'cluster' ? sortField?.dir : null,
    initialWidth: 188,
    minWidth: 188,
    cellRenderer: ({ data }: ICellRendererParams<AutoscaleVolume>) =>
      data?.cluster && data.cluster !== 'N/A' ? (
        <>
          <Tag
            variant="cluster"
            size="sm"
            text={data.cluster}
            dataTooltipId={`cluster-${data.volumeId}`}
            className="cursor-pointer"
          />
          <Tooltip id={`cluster-${data.volumeId}`} content={data.cluster} />
        </>
      ) : (
        <span className="text-primary-900">-</span>
      ),
  },
  {
    field: 'nodeGroup',
    headerName: 'Node Group',
    sort: sortField?.field === 'nodeGroup' ? sortField?.dir : null,
    initialWidth: 188,
    minWidth: 188,
    cellRenderer: ({ data }: ICellRendererParams<AutoscaleVolume>) =>
      data?.nodeGroup && data.nodeGroup !== 'N/A' ? (
        <>
          <Tag
            variant="node"
            size="sm"
            text={data.nodeGroup}
            dataTooltipId={`nodegroup-${data.volumeId}`}
            className="cursor-pointer"
          />
          <Tooltip id={`nodegroup-${data.volumeId}`} content={data.nodeGroup} />
        </>
      ) : (
        <span className="text-primary-900">-</span>
      ),
  },
  {
    field: 'instanceId',
    headerName: 'Instance ID',
    sort: sortField?.field === 'instanceId' ? sortField?.dir : null,
    initialWidth: 188,
    minWidth: 188,
    cellRenderer: ({ data }: ICellRendererParams<AutoscaleVolume>) =>
      data?.instanceId && data.instanceId !== 'N/A' ? (
        <>
          <Tag
            variant="instance"
            size="sm"
            text={data.instanceId}
            dataTooltipId={`instance-${data.volumeId}`}
            className="cursor-pointer"
          />
          <Tooltip id={`instance-${data.volumeId}`} content={data.instanceId} />
        </>
      ) : (
        <span className="text-primary-900">-</span>
      ),
  },
  {
    field: 'tags',
    headerName: 'Volume Tags',
    initialWidth: windowWidth < 1440 ? 231 : 401,
    minWidth: windowWidth < 1440 ? 231 : 401,
    flex: 1,
    valueFormatter: ({ data }) =>
      data?.tags?.map(({ key, value }) => `"${key}":"${value}"`).join(';') || '',
    cellRenderer: ({ data }: ICellRendererParams<AutoscaleVolume>) => {
      if (!data?.tags) {
        return null;
      }
      const maxVisibleTags = windowWidth < 1440 ? 1 : 2;
      return (
        <VolumeTagsRenderer
          data={{ ...data, tags: data.tags } as unknown as Volume}
          maxVisible={maxVisibleTags}
        />
      );
    },
  },
  // Current Size Column
  {
    field: 'datafyDiskSize',
    headerName: 'Current Size',
    sort: sortField?.field === 'datafyDiskSize' ? sortField?.dir : null,
    initialWidth: 134,
    minWidth: 134,
    cellRenderer: ({ data }: ICellRendererParams<AutoscaleVolume>) => {
      if (!data) {
        return 'N/A';
      }
      const sizeToUse = data.datafyDiskSize;
      return sizeToUse !== undefined && sizeToUse !== null ? <SizeCell size={sizeToUse} /> : 'N/A';
    },
  },
];
